import React, { useState } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/useToast';
import type { Equipamento } from '../../../../models';
import { equipamentoService } from '../../../../services/equipamentoService';
import { useEquipamentoMutations } from '../../../../hooks/data';
import {
  DETALHES_SITUACAO_ATIVO,
  SITUACAO_ATIVO_LABELS,
  SITUACOES_ATIVO,
  STATUS_POR_SITUACAO,
  TIPO_ATIVO_LABELS,
  TIPOS_ATIVO_CADASTRO,
  TIPOS_COM_NSERIE_OBRIGATORIO,
  rotuloPatrimonio,
  type SituacaoAtivo,
} from './ativos.definitions';
import { alertaPrefixoPropriedade } from './ativos.utils';
import { camposDoForm, erroDoForm, formDoEquipamento, formVazio, type FormFichaAtivo } from './ativoFicha.form';
import AtivoCamposTipo from './AtivoCamposTipo';
import AtivoPropriedadeCampos from './AtivoPropriedadeCampos';
import AtivoCompraCampos from './AtivoCompraCampos';
import AtivoAnexosPendentes, { type AnexoPendente } from './AtivoAnexosPendentes';
import { normalizarPatrimonio } from '../../../../models/equipamento.utils';

interface AtivoFichaFormProps {
  /** null = cadastro novo. */
  equipamento: Equipamento | null;
  onSalvo: (id: string) => void;
  /** Duplicado encontrado — o drawer troca pra ficha do existente. */
  onAbrirExistente: (id: string) => void;
  /** Aba da ficha — o form fica montado nas duas, então o que foi digitado numa não se
   * perde ao trocar pra outra; "Salvar" grava tudo. */
  /** 'anexos' só no cadastro novo (arquivos escolhidos antes de existir o ativo). */
  aba: 'dados' | 'compra' | 'anexos';
  /** Escondido (aba Histórico aberta) sem desmontar — mantém o que foi digitado. */
  oculto?: boolean;
  /** Cadastro novo já preenchido (ex.: "Cadastrar agora" a partir de uma Movimentação). */
  inicial?: Partial<FormFichaAtivo>;
}

const SITUACOES_INICIAIS: SituacaoAtivo[] = ['em_configuracao', 'em_estoque', 'em_uso', 'em_manutencao', 'inativo'];

/**
 * Cadastro/edição da ficha (discovery §3/§4). Situação NÃO é editada aqui depois de criado
 * — ela tem ação própria com motivo (AtivoSituacaoSecao); no cadastro novo, só a inicial.
 * Duplicidade: confere patrimônio (normalizado) e nº de série ao sair do campo e de novo ao
 * salvar; achando, oferece abrir o existente em vez de criar outro.
 */
export default function AtivoFichaForm({ equipamento, onSalvo, onAbrirExistente, aba, oculto, inicial }: AtivoFichaFormProps) {
  const { user } = useAuth();
  const { criarEquipamento, atualizarEquipamento } = useEquipamentoMutations();
  const [form, setForm] = useState<FormFichaAtivo>(() =>
    equipamento ? formDoEquipamento(equipamento) : { ...formVazio(), ...inicial }
  );
  const [situacaoInicial, setSituacaoInicial] = useState<SituacaoAtivo>('em_configuracao');
  const [detalheInicial, setDetalheInicial] = useState('');
  const [fornecedorInicial, setFornecedorInicial] = useState('');
  const [duplicados, setDuplicados] = useState<Equipamento[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [anexosPendentes, setAnexosPendentes] = useState<AnexoPendente[]>([]);
  const toast = useToast();

  const alterar = (campo: keyof FormFichaAtivo, valor: string) => {
    setForm((atual) => ({ ...atual, [campo]: valor }));
    setErro(null);
  };

  const conferirDuplicados = async () => {
    const encontrados = await equipamentoService.buscarDuplicados(form.patrimonio, form.nSerie, equipamento?.id);
    setDuplicados(encontrados);
    return encontrados;
  };

  const ator = user ? { id: user.collaboratorId ?? user.uid, name: user.displayName ?? user.email ?? '', avatar: null } : null;

  const salvar = async () => {
    const problema =
      erroDoForm(form) ??
      (!equipamento && situacaoInicial === 'em_manutencao' && !fornecedorInicial.trim()
        ? 'Informe o fornecedor da manutenção (onde o equipamento está).'
        : null);
    if (problema) return setErro(problema);
    setSalvando(true);
    try {
      if ((await conferirDuplicados()).length > 0) return;
      const campos = camposDoForm(form);
      if (equipamento) {
        await atualizarEquipamento.mutateAsync({ id: equipamento.id, ...campos, updatedBy: ator });
        onSalvo(equipamento.id);
      } else {
        const id = await criarEquipamento.mutateAsync({
          ...campos,
          statusCustodia: STATUS_POR_SITUACAO[situacaoInicial],
          situacaoDetalhe: detalheInicial || null,
          fornecedorManutencao: situacaoInicial === 'em_manutencao' ? fornecedorInicial.trim() || null : null,
          origem: 'manual',
          createdBy: ator,
        });
        // Arquivos escolhidos na aba Anexos antes do cadastro: sobem agora que o ativo existe.
        // Falha num arquivo não desfaz o cadastro — avisa e o SI anexa de novo na ficha.
        if (ator) {
          for (const anexo of anexosPendentes) {
            try {
              await equipamentoService.adicionarAnexo(id, anexo.arquivo, anexo.categoria, ator);
            } catch {
              toast.error(`Ativo cadastrado, mas não foi possível anexar "${anexo.arquivo.name}". Anexe de novo pela aba Anexos.`);
            }
          }
        }
        onSalvo(id);
      }
    } finally {
      setSalvando(false);
    }
  };

  const rotulo = rotuloPatrimonio(form.tipo);
  const alertaPrefixo = alertaPrefixoPropriedade(form.patrimonio, form.tipo, (form.propriedade || null) as never);
  const detalhesIniciais = DETALHES_SITUACAO_ATIVO[situacaoInicial];

  return (
    <div className={oculto ? 'hidden' : 'space-y-5'}>
      {aba === 'dados' && (
        <>
          <section className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">Tipo *</label>
              <select className="input-primary mt-1 w-full" value={form.tipo} onChange={(e) => alterar('tipo', e.target.value)}>
                <option value="">Selecione...</option>
                {TIPOS_ATIVO_CADASTRO.map((tipo) => (
                  <option key={tipo} value={tipo}>
                    {TIPO_ATIVO_LABELS[tipo]}
                  </option>
                ))}
                {form.tipo && !(TIPOS_ATIVO_CADASTRO as readonly string[]).includes(form.tipo) && (
                  <option value={form.tipo}>{TIPO_ATIVO_LABELS[form.tipo] ?? form.tipo}</option>
                )}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">{rotulo} *</label>
              <input
                className="input-primary mt-1 w-full uppercase"
                value={form.patrimonio}
                onChange={(e) => alterar('patrimonio', e.target.value.toUpperCase())}
                onBlur={conferirDuplicados}
                placeholder={form.tipo === 'chip' ? '' : 'Ex.: RLMP042'}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Nº de série{TIPOS_COM_NSERIE_OBRIGATORIO.includes(form.tipo) ? ' *' : ''}
              </label>
              <input className="input-primary mt-1 w-full" value={form.nSerie} onChange={(e) => alterar('nSerie', e.target.value)} onBlur={conferirDuplicados} />
            </div>
            {/* Chip se identifica pela operadora/linha (campos próprios) — fabricante/modelo não se aplicam. */}
            {form.tipo !== 'chip' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Fabricante</label>
                  <input className="input-primary mt-1 w-full" value={form.fabricante} onChange={(e) => alterar('fabricante', e.target.value)} />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Modelo</label>
                  <input className="input-primary mt-1 w-full" value={form.modelo} onChange={(e) => alterar('modelo', e.target.value)} />
                </div>
              </>
            )}
          </section>

          {duplicados.length > 0 && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              <p className="font-medium">Já existe cadastro com esse {rotulo.toLowerCase()} ou nº de série:</p>
              <ul className="mt-1 space-y-1">
                {duplicados.map((d) => (
                  <li key={d.id} className="flex items-center justify-between gap-2">
                    <span>
                      {normalizarPatrimonio(d.patrimonio) ?? 'Sem patrimônio'} · {TIPO_ATIVO_LABELS[d.tipo] ?? d.tipo} {d.nSerie ? `· ${d.nSerie}` : ''}
                    </span>
                    <button type="button" className="font-medium text-blue-700 hover:underline" onClick={() => onAbrirExistente(d.id)}>
                      Abrir este cadastro
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <AtivoCamposTipo form={form} onChange={alterar} />

          <AtivoPropriedadeCampos form={form} onChange={alterar} />

          {alertaPrefixo && (
            <p className="flex items-start gap-2 rounded-md bg-amber-50 p-2 text-sm text-amber-800">
              <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" />
              {alertaPrefixo}
            </p>
          )}

          {!equipamento && (
            <section className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700">Situação inicial</label>
                <select
                  className="input-primary mt-1 w-full"
                  value={situacaoInicial}
                  onChange={(e) => {
                    setSituacaoInicial(e.target.value as SituacaoAtivo);
                    setDetalheInicial('');
                  }}
                >
                  {SITUACOES_ATIVO.filter((s) => SITUACOES_INICIAIS.includes(s)).map((s) => (
                    <option key={s} value={s}>
                      {SITUACAO_ATIVO_LABELS[s]}
                    </option>
                  ))}
                </select>
              </div>
              {detalhesIniciais && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Detalhe</label>
                  <select className="input-primary mt-1 w-full" value={detalheInicial} onChange={(e) => setDetalheInicial(e.target.value)}>
                    <option value="">—</option>
                    {Object.entries(detalhesIniciais).map(([valor, label]) => (
                      <option key={valor} value={valor}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
                {situacaoInicial === 'em_manutencao' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Fornecedor da manutenção *</label>
                    <input
                      type="text"
                      className="input-primary mt-1 w-full"
                      placeholder="Onde está? Ex.: Oficina do SMART"
                      value={fornecedorInicial}
                      onChange={(e) => setFornecedorInicial(e.target.value)}
                    />
                  </div>
                )}
            </section>
          )}

        </>
      )}

      {aba === 'compra' && <AtivoCompraCampos form={form} onChange={alterar} />}
      {aba === 'anexos' && !equipamento && <AtivoAnexosPendentes anexos={anexosPendentes} onChange={setAnexosPendentes} />}

      {erro && <p className="text-sm text-red-600">{erro}</p>}
      <div className="flex justify-end">
        <button type="button" className="btn-primary" onClick={salvar} disabled={salvando}>
          {salvando ? 'Salvando...' : equipamento ? 'Salvar alterações' : 'Cadastrar ativo'}
        </button>
      </div>
    </div>
  );
}
